import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "reducers";
import { IIncident } from "./domain/IIncident";
import { IIncidentSearch } from "./domain/IIncidentSearch";
import { IIncidentType } from "./domain/IIncidentType";
import { IIncidentUpdate } from "./domain/IIncidentUpdate";
import { incidentsService } from "./services/incidents.service";
import { IPopoverState } from "../Carnets/domain/IPopoverState";

interface incidentState {
  loading: boolean;
  incidents: IIncident[];
  incidentTypes: IIncidentType[];
  totalAmount: number;
  newIncidentPopover: IPopoverState;
  searchPopover: IPopoverState;
  deleteIncidentModalOpen: boolean;
  indexForDeletion: number;
  error: any;
}

const initialState: incidentState = {
  loading: false,
  incidents: [],
  totalAmount: 0,
  incidentTypes: [],
  newIncidentPopover: { isOpen: false },
  searchPopover: { isOpen: false },
  deleteIncidentModalOpen: false,
  indexForDeletion: -1,
  error: null,
};

export const getAllIncidentsForEmployee = createAsyncThunk(
  "Incidents/getAllIncidentsForEmployee",
  async (data: IIncidentSearch | undefined) => {
    try {
      return await incidentsService.getAllIncidentsInPeriodForEmployee(data);
    } catch (err) {
      setError(err);
    }
  }
);

export const deleteIncident = createAsyncThunk(
  "Incidents/deleteIncident",
  async (incidentId: number) => {
    try {
      return await incidentsService.deleteIncident(incidentId);
    } catch (err) {
      setError(err)
    }
  }
);

export const incidentsSlice = createSlice({
  name: "incidents",
  initialState: initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setIncidents: (state, action: PayloadAction<IIncident[]>) => {
      state.incidents = action.payload;
    },
    setError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    },
    setIncident: (state, action: PayloadAction<IIncidentUpdate>) => {
      if (action.payload) {
        state.incidents = state.incidents.map((incident) =>
          incident.incidentId === action.payload.incidentId
            ? { ...incident, ...action.payload }
            : incident
        );
      }
    },
    addNewIncident: (state, action: PayloadAction<IIncident>) => {
      state.incidents = [action.payload, ...state.incidents];
    },
    setNewIncidentPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.newIncidentPopover = action.payload;
    },
    setSearchPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.searchPopover = action.payload;
    },
    setDeleteIncidentModalOpen: (
      state,
      action: PayloadAction<{ visible: boolean, index: number }>) => {
      state.deleteIncidentModalOpen = action.payload.visible;
      state.indexForDeletion = action.payload.index;
    },
    deleteIncidentById: (state, action: PayloadAction<number>) => {
      if (action.payload) {
        state.incidents = state.incidents.filter(
          (incident) => incident.incidentId !== action.payload
        );
      }
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getAllIncidentsForEmployee.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllIncidentsForEmployee.fulfilled, (state, action) => {
      if (action.payload) {
        state.incidents = action.payload.incidents;
        state.totalAmount = action.payload.totalAmount;
      }
      state.loading = false;
    });
    builder.addCase(getAllIncidentsForEmployee.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
  },
});

export const selectState = (state: RootState) => state.incidents;
export const selectIncidentForDeletion = (state: RootState) =>
  state.incidents.incidents[state.incidents.indexForDeletion];

export const {
  setLoading,
  setError,
  setIncident,
  addNewIncident,
  setNewIncidentPopover,
  setSearchPopover,
  setDeleteIncidentModalOpen,
  deleteIncidentById
} = incidentsSlice.actions;

export default incidentsSlice.reducer;
